import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
// import { TablePagination } from "@mui/material";
// import { TextField } from "@mui/material";
// import { debounce } from "lodash";

import ArticleLocationFilters, {
  ArticleLocationLinkedFiltersValue,
} from "components/ArticleLocationFilters";
import Alert from "components/utils/Alert";
import { MOVEMENT_ARTICLES_GROUPED_OUTCOME_URL } from "constants/urls";
// import { recursiveSearchFilter } from "helpers/filters";
import { getOrderClientFieldConfig } from "helpers/orderClientFields";
import useInfiniteApiList from "hooks/useInfiniteApiList";
import useCrud from "hooks/useCrud";
import { useSession } from "providers/SessionProvider";
import {
  Client,
  GroupedOutcomeMovementArticle,
  OrderDraftArticleRow,
} from "types";
import AddOrderArticleRow from "./AddOrderArticleRow";

type Props = {
  open: boolean;
  handleOpen: () => void;
  client: Client | null;
  existingRows: OrderDraftArticleRow[];
  onAdd: (row: Omit<OrderDraftArticleRow, "localId">) => void;
};

const defaultArticleLocationFilters: ArticleLocationLinkedFiltersValue = {
  locationId: undefined,
  articleCode: "",
  lote: "",
  oc: "",
};

export default function AddOrderArticleModal({
  open,
  handleOpen,
  client,
  existingRows,
  onAdd,
}: Props) {
  const { locations } = useSession();
  // const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<ArticleLocationLinkedFiltersValue>(
    defaultArticleLocationFilters,
  );
  // const [loading, setLoading] = useState(false);
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(10);

  const { fetchData: getMovementArticles } =
    useCrud<GroupedOutcomeMovementArticle>(
      MOVEMENT_ARTICLES_GROUPED_OUTCOME_URL,
      {
        disableAutoFetch: true,
      },
  );

  const getMovementArticlesRef = useRef(getMovementArticles);
  // const latestSearch = useRef("");

  useEffect(() => {
    getMovementArticlesRef.current = getMovementArticles;
  }, [getMovementArticles]);

  const stockFilters = useMemo(
    () => ({
      clientId: client?.id,
      ...filters,
    }),
    [client?.id, filters],
  );

  const fetchMovementArticleStockPage = useCallback(
    ({
      page,
      limit,
      filters: nextFilters,
    }: {
      page: number;
      limit: number;
      search: string;
      filters: typeof stockFilters;
    }) => {
      const query = new URLSearchParams({
        clientId: String(nextFilters.clientId),
        page: String(page),
        limit: String(limit),
      });

      if (nextFilters.locationId) {
        query.set("locationId", String(nextFilters.locationId));
      }

      if (nextFilters.articleCode) {
        query.set("articleCode", nextFilters.articleCode);
      }

      if (nextFilters.lote) {
        query.set("lote", nextFilters.lote);
      }

      if (nextFilters.oc) {
        query.set("oc", nextFilters.oc);
      }

      return getMovementArticlesRef.current(`?${query.toString()}`, true, {}, true);
    },
    [],
  );

  const {
    items: movementArticles,
    loading,
    loadingMore,
    hasMore,
    reset: resetMovementArticles,
    loadNextPage: loadNextMovementArticlesPage,
  } = useInfiniteApiList<GroupedOutcomeMovementArticle, typeof stockFilters>(
    {
      enabled: open && Boolean(client?.id),
      limit: 10,
      filters: stockFilters,
      getItemKey: (item) => item.group_key,
      fetchPage: fetchMovementArticleStockPage,
    },
  );

  /*
  Código anterior:
  const fetchMovementArticleStock = useCallback(
    async (searchValue = "") => {
      if (!client?.id) {
        setData([]);
        setLoading(false);
        return;
      }

      const query = queryBuilder<IMovementArticleQuery>({
        clientId: client.id,
        search: searchValue,
      });

      await getMovementArticlesRef.current(query, true);
      setLoading(false);
    },
    [client?.id, setData],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        await fetchMovementArticleStock(query);
      }, 400),
    [fetchMovementArticleStock],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);
  */

  useEffect(() => {
    if (!open) {
      return;
    }

    // setPage(0);
    // setSearch("");
    // latestSearch.current = "";
    setFilters(defaultArticleLocationFilters);

    if (!client?.id) {
      // setLoading(false);
      // setData([]);
      resetMovementArticles();
      return;
    }
  }, [open, client?.id, resetMovementArticles]);

  const filteredMovementArticles = useMemo(() => {
    return movementArticles;
  }, [movementArticles]);

  /*
  const filteredMovementArticles = useMemo(() => {
    if (search.length < 3) {
      return movementArticles;
    }

    return (
      recursiveSearchFilter(
        search,
        movementArticles,
        ["article_locations"],
        [
          ["article", "code"],
          ["article", "description"],
        ],
      ) || []
    );
  }, [movementArticles, search]);
  */

  // const paginatedMovementArticles = useMemo(
  //   () =>
  //     filteredMovementArticles.slice(
  //       page * rowsPerPage,
  //       page * rowsPerPage + rowsPerPage,
  //     ),
  //   [filteredMovementArticles, page, rowsPerPage],
  // );

  const clientFieldConfig = useMemo(
    () => getOrderClientFieldConfig(client),
    [client],
  );
  const showLote = clientFieldConfig.detail.showLote;
  const showOc = clientFieldConfig.detail.showOc;
  const showFechaVencimiento = clientFieldConfig.detail.showFechaVencimiento;

  const handleClose = () => {
    // debouncedSearch.cancel();
    // setSearch("");
    // latestSearch.current = "";
    setFilters(defaultArticleLocationFilters);
    // setPage(0);
    // setLoading(false);
    // setData([]);
    resetMovementArticles();
    handleOpen();
  };

  const handleFiltersChange = (nextFilters: ArticleLocationLinkedFiltersValue) => {
    setFilters(nextFilters);
    // setPage(0);
    // setLoading(true);
    // void fetchMovementArticleStock(nextFilters);
  };

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || loadingMore) {
        return;
      }

      const target = event.currentTarget;
      const reachedBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (reachedBottom) {
        loadNextMovementArticlesPage();
      }
    },
    [hasMore, loadingMore, loadNextMovementArticlesPage],
  );

  /*
  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const nextSearch = event.target.value;

    setSearch(nextSearch);
    setPage(0);

    debouncedSearch.cancel();

    if (nextSearch.length < 3) {
      if (latestSearch.current !== "") {
        latestSearch.current = "";
        setLoading(true);
        void fetchMovementArticleStock();
        return;
      }

      setLoading(false);
      return;
    }

    latestSearch.current = nextSearch;
    setLoading(true);
    void debouncedSearch(nextSearch);
  };
  */

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {/*
            <TextField
              id="order-article-search"
              label="Buscar articulo"
              fullWidth
              value={search}
              onChange={handleSearchChange}
            />
            */}
            <ArticleLocationFilters
              clientId={client?.id}
              value={filters}
              onChange={handleFiltersChange}
              disabled={loading}
            />
          </Grid>

          {loading ? (
            <Grid item xs={12} display="flex" justifyContent="center">
              <CircularProgress />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TableContainer
                component={Paper}
                sx={{ maxHeight: "24rem" }}
                onScroll={handleTableScroll}
              >
                {!client ? (
                  <Alert
                    severity="info"
                    text="Seleccione un cliente antes de agregar artículos."
                  />
                ) : filteredMovementArticles.length > 0 ? (
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" style={{ minWidth: 140 }}>
                          Codigo
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 220 }}>
                          Descripcion
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 140 }}>
                          Unidades por pallet
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 110 }}>
                          UM
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 140 }}>
                          Presentación
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 140 }}>
                          Cantidad
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 170 }}>
                          Cantidad por Contenedor
                        </TableCell>
                        {showLote && (
                          <TableCell align="center" style={{ minWidth: 150 }}>
                            Lote
                          </TableCell>
                        )}
                        <TableCell align="center" style={{ minWidth: 170 }}>
                          Fecha Ingreso
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 150 }}>
                          Ubicación
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 170 }}>
                          Sub-ubicación
                        </TableCell>
                        {showOc && (
                          <TableCell align="center" style={{ minWidth: 150 }}>
                            Orden de Compra
                          </TableCell>
                        )}
                        {showFechaVencimiento && (
                          <TableCell align="center" style={{ minWidth: 170 }}>
                            Fecha de Vencimiento
                          </TableCell>
                        )}
                        <TableCell align="center" style={{ minWidth: 130 }}>
                          Total Unidad
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 130 }}>
                          Subtotal (Kg)
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 130 }}>
                          Observaciones
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 120 }}>
                          Disponible
                        </TableCell>
                        <TableCell align="center" style={{ minWidth: 100 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMovementArticles.map((groupedMovementArticle) => (
                        <AddOrderArticleRow
                          key={groupedMovementArticle.group_key}
                          groupedMovementArticle={groupedMovementArticle}
                          client={client}
                          locations={locations}
                          existingRows={existingRows}
                          onAdd={onAdd}
                        />
                      ))}
                      {loadingMore && (
                        <TableRow>
                          <TableCell colSpan={17} align="center">
                            Cargando...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <Alert
                    severity="info"
                    text="No hay artículos con stock disponible para este cliente."
                  />
                  /*
                  search.length >= 3 ? (
                    <Alert
                      severity="warning"
                      text="No se encontraron artículos con stock para esta búsqueda."
                    />
                  ) : (
                    <Alert
                      severity="info"
                      text="No hay artículos con stock disponible para este cliente."
                    />
                  )
                  */
                )}
              </TableContainer>

              {/*
              {filteredMovementArticles.length > 0 && (
                <TablePagination
                  component="div"
                  count={filteredMovementArticles.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              )}
              */}
            </Grid>
          )}

          <Grid item xs={12} display="flex" justifyContent="end" gap={2}>
            <Button
              variant="outlined"
              onClick={handleClose}
              data-cy="finish-adding-order-articles-button"
            >
              Cerrar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
